import dynamic from 'next/dynamic'
import * as R from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'

import MaxWidth from '../../components/Layout/MaxWidth'
import NextSeo from '../../components/NextSeo'
import PartyInfo from '../../components/PartyInfo'
import {
  CorporateDetailSkeleton,
  CorporateRelatedArticlesSkeleton
} from '../../components/Skeleton'
import { useMq, useTracking } from '../../hooks'
import { withTranslation } from '../../i18n'
import { getArticleIds } from '../../redux/article/selectors'
import { getCorporateDetailRequest } from '../../redux/corporate/actions'
import { getCorporateDetail } from '../../redux/corporate/selectors'
import { clearRedux } from '../../redux/master/actions'
import { hk01LinkHandler, resizeImg } from '../../utils/commonUtils'
import { getColor, getFontSize } from '../../utils/cssUtils'
import { corporateDetailView } from '../../utils/dataTracker'
import mq from '../../utils/mediaQuery'

const Wrapper = styled(MaxWidth)`
  margin-top: 24px;
  margin-bottom: 40px;
  width: 100%;
`
const Avatar = styled.div`
  height: 80px;
  width: 165px;
  ${({ avatar }) =>
    avatar &&
    css`
      background-image: url('${resizeImg(avatar, 320)}');
    `}
  background-position: center center;
  background-size: contain;
  background-repeat: no-repeat;
  position: relative;
  flex-shrink: 0;
`
const Headline = styled.h1`
  font-size: ${getFontSize('headline')};
  width: 100%;
  line-height: 1.45;
  font-weight: 600;
  color: ${getColor('black')};
  word-break: break-word;
  ${mq.mobile} {
    margin: 8px auto 0;
    text-align: center;
  }
  a {
    transition: color 0.1s linear;
    color: ${getColor('black')};
    &:hover {
      color: ${getColor('theme')};
    }
  }
`
const SubHeadline = styled.h2`
  font-size: ${getFontSize('body2')};
  margin-bottom: 8px;
  text-align: left;
  color: ${getColor('black')};
`
const Description = styled.p`
  margin-bottom: 24px;
  text-align: left;
  color: ${getColor('grey')};
`
const ActivityDescription = styled.div`
  color: ${getColor('grey')};
  word-break: break-word;
  text-align: left;
  img {
    width: 100%;
    max-width: 588px;
  }
`

const DynamicRelatedArticles = dynamic(
  () => import('../../components/Corporate/RelatedArticles'),
  { loading: () => <CorporateRelatedArticlesSkeleton />, ssr: false }
)

const corporateDetail = withTranslation('common')(({ t, corporateNo }) => {
  const dispatch = useDispatch()

  const track = useTracking()

  const { isMobile, isTablet, isDesktop } = useMq()

  const corporateDetail = useSelector(getCorporateDetail)

  const articleIds = useSelector(getArticleIds) || []

  const [currentPage, setCurrentPage] = useState(1)

  const pageSize = useMemo(
    () => (isMobile ? 1 : isTablet ? 2 : isDesktop ? 6 : 6),
    [isMobile, isTablet, isDesktop]
  )

  const pagination = useMemo(
    () => R.unless(R.isNil, R.splitEvery(R.__, articleIds))(pageSize),
    [articleIds, pageSize]
  )

  const current = R.min(
    currentPage,
    pagination ? R.length(pagination) : Infinity
  )

  const handleContentLinkClick = useCallback(hk01LinkHandler, [])

  const {
    nameChi = null,
    description = null,
    logoImageFile: { url: logoUrl } = { logoUrl: null },
    activityDescription = null
  } = corporateDetail || {}

  useEffect(() => {
    track(corporateDetailView(corporateNo), true)
    return () => {
      dispatch(clearRedux(['corporateState', 'result', 'corporate']))
      dispatch(clearRedux(['articleState', 'result']))
    }
  }, [])

  return (
    <>
      <NextSeo
        title={t('seo.corporate.detail.ogTitle')}
        description={t('seo.corporate.detail.ogDescription', {
          corporateDisplayName: nameChi
        })}
      />
      <Wrapper>
        {R.isNil(corporateDetail) ? (
          <CorporateDetailSkeleton />
        ) : (
          <PartyInfo
            avatar={<Avatar avatar={logoUrl} />}
            title={<Headline>{nameChi}</Headline>}
          >
            <SubHeadline>{t('corporate.description')}</SubHeadline>
            <Description>{description}</Description>
            {activityDescription && (
              <SubHeadline>{t('corporate.activityDescription')}</SubHeadline>
            )}
            <ActivityDescription
              dangerouslySetInnerHTML={{ __html: activityDescription }}
              onClick={handleContentLinkClick}
            />
          </PartyInfo>
        )}
        {pageSize && !R.isEmpty(pagination) && (
          <DynamicRelatedArticles
            articleIds={articleIds}
            current={current}
            pageSize={pageSize}
            pagination={pagination}
            setCurrentPage={setCurrentPage}
            corporateNo={corporateNo}
          />
        )}
      </Wrapper>
    </>
  )
})

corporateDetail.getInitialProps = async ({ query, store }) => {
  const { corporateNo } = query

  if (R.isNil(corporateNo) || !R.test(/^CORP\d{5}$/, corporateNo)) {
    const err = new Error()

    err.code = 'ENOENT'

    throw err
  }

  store.dispatch(getCorporateDetailRequest(encodeURIComponent(corporateNo)))

  return {
    corporateNo
  }
}

export default corporateDetail
